import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  DRILLDOWN_SEARCH_DEBOUNCE_MS,
  DRILLDOWN_SEARCH_MAX_LENGTH,
  DRILLDOWN_VIEWS,
  REPURCHASE_FILTER_SEGMENTS,
  isRepurchaseFilterSegment,
  normalizeDrilldownSearch,
  type DrilldownView,
  type DrilldownOrigin,
  type RepurchaseFilterSegment,
  type RepurchaseSegment,
} from '@/lib/orders-drilldown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OrdersDrilldownSlidingToggle } from '@/components/orders-drilldown/orders-drilldown-sliding-toggle';
import {
  CHANNEL_MIGRATION_COLOR,
  IN_APP_REPURCHASE_COLOR,
} from '@/components/orders-composition-donut';

interface OrdersDrilldownToolbarProps {
  /** What opened the drill-down. */
  segment: DrilldownOrigin;
  /** Shows the repurchase segment filter. */
  isRepurchase: boolean;
  view: DrilldownView;
  /** Search currently applied to the data (already normalized). */
  search: string | null;
  onSegmentChange: (segment: RepurchaseSegment) => void;
  onViewChange: (view: DrilldownView) => void;
  /** Called once the user stops typing; receives the normalized text. */
  onSearchChange: (search: string | null) => void;
}

export function OrdersDrilldownToolbar({
  segment,
  isRepurchase,
  view,
  search,
  onSegmentChange,
  onViewChange,
  onSearchChange,
}: OrdersDrilldownToolbarProps) {
  const { LL } = useI18nContext();

  const [input, setInput] = useState(search ?? '');
  const debouncedInput = useDebouncedValue(input, DRILLDOWN_SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    const next = normalizeDrilldownSearch(debouncedInput);
    if (next !== search) onSearchChange(next);
    // Only the debounced text should trigger a search: re-running when
    // `search` changes would fight the user's in-flight typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput]);

  const viewLabels: Record<DrilldownView, string> = {
    orders: LL.metricsPage.repurchaseDrilldown.toolbar.viewOrders(),
    customers: LL.metricsPage.repurchaseDrilldown.toolbar.viewCustomers(),
  };

  const segmentLabels: Record<RepurchaseFilterSegment, string> = {
    total_repurchase: LL.metricsPage.repurchaseDrilldown.segmentFilter.total(),
    in_app: LL.metricsPage.repurchaseDrilldown.segmentFilter.in_app(),
    channel_migration:
      LL.metricsPage.repurchaseDrilldown.segmentFilter.channel_migration(),
  };

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
      {isRepurchase && isRepurchaseFilterSegment(segment) ? (
        <OrdersDrilldownSlidingToggle
          layoutId="repurchase-segment-pill"
          getColor={(option) =>
            option === 'in_app'
              ? IN_APP_REPURCHASE_COLOR
              : option === 'channel_migration'
                ? CHANNEL_MIGRATION_COLOR
                : undefined
          }
          value={segment}
          options={REPURCHASE_FILTER_SEGMENTS}
          getLabel={(option) => segmentLabels[option]}
          onChange={onSegmentChange}
          ariaLabel={LL.metricsPage.repurchaseDrilldown.segmentFilter.label()}
        />
      ) : null}

      <OrdersDrilldownSlidingToggle
        layoutId="repurchase-view-pill"
        value={view}
        options={DRILLDOWN_VIEWS}
        getLabel={(option) => viewLabels[option]}
        onChange={onViewChange}
        ariaLabel={LL.metricsPage.repurchaseDrilldown.toolbar.viewLabel()}
      />

      <div className="relative min-w-[200px] flex-1">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={input}
          maxLength={DRILLDOWN_SEARCH_MAX_LENGTH}
          onChange={(event) => setInput(event.target.value)}
          placeholder={LL.metricsPage.repurchaseDrilldown.toolbar.searchPlaceholder()}
          aria-label={LL.metricsPage.repurchaseDrilldown.toolbar.searchAriaLabel()}
          className="px-9 [&::-webkit-search-cancel-button]:hidden"
        />
        {input.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 size-7 -translate-y-1/2"
            aria-label={LL.metricsPage.repurchaseDrilldown.toolbar.clearSearch()}
            onClick={() => setInput('')}
          >
            <X className="size-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
